// Loose domain types for the admin UI. The backend returns rich nested
// documents; we type the fields the admin screens actually read and leave the
// rest open. Kept deliberately permissive to avoid contract drift.
import type { Bilingual } from "./api";
import type { PartPaymentSplit } from "@safiri/shared";

export type Loose<T> = T & Record<string, unknown>;

// ---- Driver ----
export type DriverAccountStatus =
  | "documentPending"
  | "underApproval"
  | "approved"
  | "rejected"
  | "documentExpired"
  | "suspended"
  | "locked"
  | "deleted"
  | "pending";

export type TripAvailability = "online" | "offline" | "underTrip";

// Reviewable documents. Vehicle registration is not one — the plate number is
// a `vehicleDetails.registrationNumber` field, not a document.
export type DriverDocumentType =
  | "licenseDetails"
  | "latraVehicleLicenseD4Details"
  | "vehicleInsuranceDetails";

export type DriverDocumentStatus =
  | "pending"
  | "underReview"
  | "approved"
  | "rejected";

export type DocumentRejectionReason =
  | "invalidDocument"
  | "wrongSlot"
  | "expiredDocument"
  | "fraudulentDocument"
  | "vehicleMismatch"
  | "other";

export interface DocDetail {
  imageUrl?: string;
  frontImageUrl?: string;
  /** Set by an admin at approval — drivers no longer supply one. */
  expiryDate?: string;
  licenseNumber?: string;
  isExpired?: boolean;
  // Per-document review trail. Absent on drivers reviewed before this shipped.
  status?: DriverDocumentStatus;
  rejectionReason?: DocumentRejectionReason;
  rejectionComment?: string;
  reviewedBy?: string;
  reviewedAt?: string;
}

export interface Driver {
  _id: string;
  id?: string;
  /** Admin-only identification tags; present on the admin list. */
  tags?: import("@safiri/shared").AssignedTagDto[];
  personalDetails?: {
    name?: string;
    email?: string;
    phoneNumber?: string;
    dob?: string;
    gender?: string;
    profilePicUrl?: string;
  };
  vehicleDetails?: {
    make?: string;
    model?: string;
    color?: string;
    latraVehicleType?: string;
    vehicleCategoryID?: string;
    /** Plate number — a vehicle attribute, not a document. */
    registrationNumber?: string;
    chassisNumber?: string;
    vehicleImages?: string[];
  };
  // Backend field is `documents` (not `documentDetails`). No
  // vehicleRegistrationDetails slot — the plate number lives on
  // vehicleDetails above, not here.
  documents?: {
    licenseDetails?: DocDetail;
    latraVehicleLicenseD4Details?: DocDetail;
    vehicleInsuranceDetails?: DocDetail;
  };
  accountStatusDetails?: {
    currentStatus?: DriverAccountStatus;
    history?: Array<{
      status?: string;
      reason?: string;
      updatedAt?: string;
      updatedBy?: string;
    }>;
  };
  faceVerification?: {
    lastSelfieKey?: string;
    lastVerifiedAt?: string;
    nextDueAt?: string;
    lastScore?: number;
    failedAttempts?: number;
    lastFailedAt?: string;
  };
  ratingDetails?: { currentRating?: number };
  lifeTimeStats?: {
    totalTrip?: number;
    totalEarning?: number;
    totalDistance?: number;
  };
  financialDetails?: {
    /** Signed: positive = driver owes Safiri, negative = Safiri owes driver. */
    platformDebt?: number;
    isLockedDueToDebt?: boolean;
    lastDebtUpdate?: string;
    gracePeriodEndDate?: string;
    /** null/undefined = use the global Settings.bookingConfig.maxDriverDebt. */
    maxDriverDebtOverride?: number | null;
    unallocatedSettlementCredit?: number;
  };
  // Backend field is `tripAvailabilityStatus` (online|offline|underTrip).
  tripAvailabilityStatus?: TripAvailability;
  createdAt?: string;
  updatedAt?: string;
}

// ---- Rider / User ----
export type UserStatus = "active" | "suspended" | "banned";

export interface AppUser {
  _id: string;
  id?: string;
  /** Admin-only identification tags; present on the admin list. */
  tags?: import("@safiri/shared").AssignedTagDto[];
  personalDetails?: {
    name?: string;
    email?: string;
    phoneNumber?: string;
    gender?: string;
    dob?: string;
    profilePicUrl?: string;
  };
  referralDetails?: { code?: string; usersReferred?: unknown[] };
  ratingDetails?: { currentRating?: number };
  statusDetails?: {
    currentStatus?: UserStatus;
    history?: Array<{ status?: string; reason?: string; updatedAt?: string }>;
  };
  walletBalance?: number;
  createdAt?: string;
}

// ---- Trip (matches the real API shape) ----
export interface TripStop {
  name?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  arrivedAt?: string | null;
  departedAt?: string | null;
  skipped?: boolean;
}

export interface Trip {
  _id: string;
  tripId?: string;
  tripRefNumber?: number;
  tripStatus?: string;
  passengerDetail?: {
    passengerID?: string;
    name?: string;
    phoneNumber?: string;
    profilePicUrl?: string;
    rating?: number;
  };
  driverDetail?: {
    driverID?: string;
    name?: string;
    phoneNumber?: string;
    profilePicUrl?: string;
    rating?: number;
    vehicleDetail?: {
      make?: string;
      model?: string;
      color?: string;
      vehicleRegistrationNumber?: string;
      latraVehicleType?: string;
      /** The driver's own ride type, snapshotted when they accepted. */
      vehicleCategoryID?: string;
    };
  };
  rideTypeDetail?: {
    id?: string;
    info?: { name?: Bilingual };
    rideFareDetails?: {
      actualFare?: number;
      grossFare?: number;
      netFare?: number;
      discount?: number;
      roundingDifference?: number;
      driverEarning?: number;
      platformCommission?: number;
      platformEarning?: number;
      bookingFee?: number;
    };
  };
  paymentDetail?: {
    paymentMode?: string;
    pricingDetail?: {
      netFare?: number;
      actualFare?: number;
      grossFare?: number;
      discount?: number;
      roundingDifference?: number;
      driverEarning?: number;
      platformCommission?: number;
      bookingFee?: number;
      /** Rider-paid LATRA levy (pass-through). */
      latraLevy?: number;
      /** Itemised fare: base + distance + time + waiting = actualFare. */
      baseCharge?: number;
      distanceCharge?: number;
      timeCharge?: number;
      platformEarning?: number;
      vatOnCommission?: number;
      vatOnBookingFee?: number;
      paymentGatewayFee?: number;
      tollFee?: number;
      waitingFee?: number;
      cancellationFee?: number;
    };
    /** Part wallet / part cash; paymentMode stays "wallet". */
    split?: PartPaymentSplit | null;
    cashSettlement?: "adminClosedUnpaid" | null;
    dpoMetadata?: { status?: string };
  };
  statistics?: {
    estimatedDistance?: number;
    estimatedDuration?: number;
    actualDistance?: number | null;
    actualDuration?: number | null;
    waitingDuration?: number | null;
  };
  initialTripRoute?: TripStop[];
  finalTripRoute?: TripStop[];
  bookedFor?: string;
  cancelDetail?: {
    createdByInfo?: { userType?: string; createdBy?: string };
    cancelledAt?: string;
    reason?: string;
    cancellationFee?: number;
    cancellationFeeStatus?: "pending" | "applied" | "settled" | "waived";
    cancellationFeeTripId?: string | null;
  } | null;
  passengerRatingForDriver?: { rating?: number; message?: string; ratedAt?: string };
  driverRatingForPassenger?: { rating?: number; message?: string; ratedAt?: string };
  shareToken?: string;
  shareUrl?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface TripStats {
  totalTrips?: number;
  completedTrips?: number;
  cancelledTrips?: number;
  totalRevenue?: number;
  averageRating?: number;
  singleStopTrips?: number;
  multiStopTrips?: number;
}

// ---- Ride type / pricing ----
export interface RideType {
  _id: string;
  id?: string;
  name?: Bilingual;
  description?: Bilingual;
  vehicleType?: "taxi" | "bike" | "bajaj";
  // Backend enum is lowercase `car | motorcycle | tricycle` (types/rideType.ts).
  // This union previously said "CAR" | "BICYCLE" | "TRICYCLE", which matched
  // neither the case nor the values.
  latraVehicleType?: "car" | "motorcycle" | "tricycle";
  minimumFare?: number;
  /** Required by the backend on create; was missing from this type and the form. */
  fareUnderOneKm?: number;
  farePerKm?: number;
  farePerMin?: number;
  waitingFeePerMin?: number;
  /** Flat fee when the rider cancels after the driver arrived (multiple of 500). */
  cancellationFee?: number;
  /** Optional per-category commission override; null = use the global default. */
  commissionPercentage?: number | null;
  /** Ride type ids this one's drivers may also take trips for (XL → Haraka). */
  alsoServes?: string[];
  numberOfPassengers?: number;
  imageUrl?: string;
  position?: number;
  isActive?: boolean;
  isServicable?: boolean;
}

// ---- Settings ----
export interface BookingConfig {
  freeWaitingMinute?: number;
  /** Straight-line metres from pickup within which "arrived" is accepted. */
  pickupArrivalRadiusMeters?: number;
  /** Straight-line metres from a stop within which "arrived at stop" is accepted. */
  stopArrivalRadiusMeters?: number;
  /** LEGACY: old monthly fee on collected commission, replaced by latraLevyPercentage. */
  latraFeePercentage?: number;
  /** Default commission rate; a RideType may override it per category. */
  safiriCommissionPercentage?: number;
  bookingFeePercentage?: number;
  /** Rider-paid LATRA levy, % of the actual fare (passed on to LATRA). */
  latraLevyPercentage?: number;
  vatPercentage?: number;
  /** Three independent, additive gateway fees. */
  ridePaymentGatewayFeePercentage?: number;
  walletTopUpGatewayFeePercentage?: number;
  driverPayoutGatewayFeePercentage?: number;
  /** Default debt ceiling; overridable per driver. */
  maxDriverDebt?: number;
  /**
   * Minimum credit balance a driver must reach before they may cash out.
   * Whole TZS. Global only — there is deliberately no per-driver override.
   */
  minimumPayoutThresholdAmount?: number;
  /**
   * Upfront-pricing corridor: the fare quoted at booking stays the final
   * charge as long as the actual recalculated fare stays within this band
   * around it. Increase and decrease sides are distinct percentages.
   */
  upfrontFareIncreaseThresholdPercentage?: number;
  upfrontFareDecreaseThresholdPercentage?: number;
  /**
   * Part wallet / part cash. Keep off until the force-updated rider and
   * driver apps are mandatory: an older driver app would ask for the full
   * fare in cash after the wallet part was already taken.
   */
  partWalletEnabled?: boolean;
  /** The cash due is always a multiple of this (TZS). */
  partWalletCashStep?: number;
  /** Hours before a debited-but-unconfirmed trip shows in the inbox. */
  partWalletReviewHours?: number;
}

/** A driver whose debt trip block disagrees with their balance. */
export interface DebtLimitDriver {
  driverId: string;
  name: string;
  phoneNumber: string;
  platformDebt: number;
  maxDebt: number;
  hasOverride: boolean;
}

/** GET /settings/debt-limit/impact — who a global ceiling would affect. */
export interface DebtLimitImpact {
  globalMax: number;
  toBlock: DebtLimitDriver[];
  toUnblock: DebtLimitDriver[];
  /** Still auto-LOCKED by the old behaviour; a sync moves them to APPROVED. */
  legacyLocked: DebtLimitDriver[];
}

export interface DebtLimitSyncResult {
  globalMax: number;
  checked: number;
  blocked: number;
  unblocked: number;
  failed: number;
}

export interface SafetyConfig {
  /** E.164. Unset means SOS reaches personal emergency contacts only. */
  policeEmergencyNumber?: string;
  /** How long a driver's selfie verification stays valid. */
  driverSelfieVerificationDays?: number;
}

export interface Settings {
  _id?: string;
  bookingConfig?: BookingConfig;
  safetyConfig?: SafetyConfig;
}

// ---- Commission ledger ----
export type CommissionStatus = "pending" | "collected";

export type CommissionCollectionMethod =
  | "auto-online"
  | "auto-wallet"
  | "gateway-payout"
  | "manual";

export interface CommissionRecord {
  _id: string;
  tripID?: string;
  driverID?: string;
  paymentMode?: string;
  platformCommission?: number;
  vatOnCommission?: number;
  bookingFee?: number;
  vatOnBookingFee?: number;
  /** Rider-paid LATRA levy on this trip (0 on older records). */
  latraLevy?: number;
  platformEarning?: number;
  driverEarning?: number;
  debtDelta?: number;
  totalCollectible?: number;
  status?: CommissionStatus;
  completedAt?: string;
  collectedAt?: string | null;
  collectionMethod?: CommissionCollectionMethod | null;
  taxReceiptID?: string | null;
  createdAt?: string;
}

export interface DriverBalanceSummary {
  platformDebt: number;
  pendingCommission: number;
  unallocatedCredit: number;
  maxDebt: number;
  isLockedDueToDebt: boolean;
}

// ---- Tax receipts (VFD) ----
export interface TaxReceipt {
  _id: string;
  collectionRef?: string;
  custinvoiceno?: string;
  collectionMethod?: CommissionCollectionMethod;
  tripId?: string | null;
  tripCount?: number;
  subjectType?: "driver" | "passenger";
  driverId?: string | null;
  driverName?: string;
  driverPhone?: string;
  commissionAmount?: number;
  bookingFeeAmount?: number;
  taxableAmount?: number;
  vatAmount?: number;
  lineItems?: { description: string; qty: string; taxcode: string; amt: number }[];
  status?: "pending" | "synced" | "failed" | "skipped";
  receiptNumber?: string | null;
  verificationUrl?: string | null;
  attempts?: number;
  lastError?: string | null;
  issuedAt?: string | null;
  createdAt?: string;
}

// ---- LATRA monthly remittance ----
export interface LatraMonthlyReport {
  period: string;
  periodStart: string;
  periodEnd: string;
  /** Rider levy rate (% of actual fare) for the month. */
  latraLevyPercentage: number;
  /** Informational: commission collected in the month. */
  collectedCommissionBase: number;
  collectedVatOnCommission: number;
  collectedBookingFee: number;
  /** Rider-paid LATRA levy collected in the month. */
  collectedLatraLevy: number;
  /** Payable to LATRA: the levy collected. */
  latraAmount: number;
  tripCount: number;
  driverCount: number;
  status: string;
  referenceNumber?: string | null;
  syncedAt?: string | null;
  breakdown: {
    day: string;
    paymentMode: string;
    commission: number;
    latraLevy: number;
    tripCount: number;
  }[];
  isProvisional: boolean;
}

// ---- Toll Location ----
export type TollVehicleType = "car" | "motorcycle" | "tricycle";

export interface TollLocation {
  _id: string;
  name?: Bilingual;
  center?: {
    type: "Point";
    coordinates: [number, number]; // [longitude, latitude]
  };
  radiusMeters?: number;
  feeTZS?: number;
  appliesToVehicleTypes?: TollVehicleType[];
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// ---- Transaction ----
export interface Transaction {
  _id: string;
  transactionType?: string;
  status?: string;
  paymentMode?: string;
  category?: string;
  amount?: number;
  reason?: Bilingual | string;
  remarks?: string;
  userID?: string;
  driverID?: string;
  tripID?: string;
  createdAt?: string;
}

// ---- SOS (an alert event, not a status-managed queue) ----
export interface SOSAlert {
  _id: string;
  occurredByID?: string;
  name?: string;
  phoneNumber?: string;
  occurredBy?: string; // DRIVER | RIDER
  location?: { latitude?: number; longitude?: number };
  occurredAt?: string;
  tripID?: MaybePopulated;
  status?: string; // exists on the model but not surfaced as a queue
  createdAt?: string;
}

// ---- Coupon ----
export interface Coupon {
  _id: string;
  couponCode?: string;
  description?: Bilingual;
  frequency?: number;
  discountPercentage?: number;
  maxDiscountAmount?: number;
  minApplicableAmount?: number;
  endDate?: string;
  isActive?: boolean;
  isDefault?: boolean;
  createdAt?: string;
}

// A reference field that may be a raw id (string) or a populated document.
export type MaybePopulated =
  | string
  | {
      _id?: string;
      tripId?: string;
      name?: string;
      phoneNumber?: string;
      email?: string;
      personalDetails?: { name?: string; phoneNumber?: string };
    }
  | null;

/** Read a display name from a possibly-populated user/driver ref. */
export function refName(r: MaybePopulated | undefined): string | undefined {
  if (!r || typeof r === "string") return undefined;
  return r.personalDetails?.name ?? r.name;
}
/** Read a phone from a possibly-populated user/driver ref. */
export function refPhone(r: MaybePopulated | undefined): string | undefined {
  if (!r || typeof r === "string") return undefined;
  return r.personalDetails?.phoneNumber ?? r.phoneNumber;
}

// ---- Lost item & Query (support) ----
export interface LostItem {
  _id: string;
  ticketNo?: string;
  itemType?: string;
  message?: string;
  status?: string;
  tripID?: MaybePopulated;
  userID?: MaybePopulated;
  driverID?: MaybePopulated;
  images?: string[];
  history?: Array<{ status?: string; note?: string; updatedAt?: string }>;
  createdAt?: string;
}

export interface SupportQuery {
  _id: string;
  ticketNo?: string;
  queryType?: string;
  message?: string;
  status?: string;
  createdByInfo?: { userType?: string; createdBy?: MaybePopulated };
  tripID?: MaybePopulated;
  images?: string[];
  history?: Array<{ status?: string; note?: string; updatedAt?: string }>;
  createdAt?: string;
}

// ---- Driver cash-outs (gateway-executed disbursements) ----

export type DriverPayoutStatus =
  | "PENDING"
  | "PROCESSING"
  | "COMPLETED"
  | "FAILED";

/**
 * A cash-out is stored as a Transaction with category `driverPayout`, so it
 * shares that shape. The payout-specific lifecycle lives on `payload`, and is
 * FINER-GRAINED than the transaction `status`: a PROCESSING payout is still a
 * PENDING transaction, because the money is in flight but not delivered.
 */
export interface DriverPayout {
  _id: string;
  amount: number;
  status: "PENDING" | "SUCCESS" | "FAILED";
  category: "driverPayout";
  driverID?:
    | string
    | {
        _id: string;
        personalDetails?: { name?: string; phoneNumber?: string };
      };
  remarks?: string;
  createdAt?: string;
  updatedAt?: string;
  payload?: {
    provider?: string;
    payoutStatus?: DriverPayoutStatus;
    /** Exactly what was posted to the driver's balance, for auditing. */
    debtDeltaApplied?: number;
    debtDeltaReversed?: number;
    /** Set when the gateway never confirmed acceptance. */
    needsReconciliation?: boolean;
    lastError?: string;
    /** Safiri absorbs the disbursement fee; the driver is never deducted. */
    feeAbsorbedBySafiri?: boolean;
    destination?: {
      destinationID?: string;
      type?: string;
      fiCode?: string;
      accountNumber?: string;
      accountName?: string;
      label?: string;
    };
    selcom?: { status?: string; receipt?: string; message?: string };
    settledAt?: string;
    failedAt?: string;
  };
}

// ---- Admin-managed Selcom payout destination catalogue ----

export type PayoutDestinationCategory = "bank" | "mobile-money" | "selcom-pesa";
export type SelcomReferenceType = "NUMERIC" | "ALPHANUMERIC";

/**
 * A destination a driver can pick when saving/paying out to a bank or mobile
 * money account. `shortcode` is immutable once created — saved driver
 * destinations reference it by string, so renaming it would orphan them.
 */
export interface SelcomCatalogueEntry {
  _id: string;
  name: string;
  shortcode: string;
  category: PayoutDestinationCategory;
  referenceType: SelcomReferenceType;
  lookupEnabled: boolean;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}
